// src/pages/admin/templates/CreateOrEditTemplate.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Cropper from "react-easy-crop";
import { CycleDialog } from "./../../../components/ShowCyclicDep";
import type { RootState } from "../../../store";
import { db } from "../../../config/firebaseConfig";
import getCroppedImg from "../../../util/getCroppedImg";
import {
  openingInitial,
  closingInitial,
  templateStructure,
} from "../../../util/contentStrings";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  ChevronLeftIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { Select } from "../../../components/catalyst/select";
import MarkdownSectionEditor from "../../../components/MarkdownSectionEditor";
import { Button } from "../../../components/catalyst/button";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// ⬇️ Firebase Storage for optional image upload on Save Draft
import { getStorage, ref as storageRef, uploadString, getDownloadURL } from "firebase/storage";

type LayerDoc = { id: string; layerNo: string; title: string; short?: string };
type TemplateOption = { id: string; title: string };
type AnnexureDoc = { id: string; number: string; title: string; requiresConsent?: boolean };

const normKey = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, " ");

// ---- Lightweight Modal (used for Layer/Annexure “View”) ----
function SimpleModal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-base font-semibold text-zinc-800">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm text-zinc-600 hover:bg-zinc-100"
          >
            Close
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto px-5 py-4">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function CreateOrEditTemplate() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);

  // form state
  const [title, setTitle] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [price, setPrice] = useState<string>(""); // allow "0"
  const [priceError, setPriceError] = useState("");
  const [isRelationShipReport, setIsRelationShipReport] =
    useState<"yes" | "no">("yes");
  const [hasPredecessors, setHasPredecessors] = useState<"yes" | "no">("no");
  const [predecessorIds, setPredecessorIds] = useState<string[]>([""]);
  const [structure, setStructure] = useState(templateStructure);
  const [openingInitialValue, setOpeningInitialValue] =
    useState(openingInitial);
  const [closingInitialValue, setClosingInitialValue] =
    useState(closingInitial);

  // mapping
  const [layers, setLayers] = useState<LayerDoc[]>([]);
  const [selectedLayerIds, setSelectedLayerIds] = useState<Set<string>>(
    new Set()
  );

  // NEW: annexures mapping
  const [annexures, setAnnexures] = useState<AnnexureDoc[]>([]);
  const [selectedAnnexureIds, setSelectedAnnexureIds] = useState<Set<string>>(
    new Set()
  );

  // options / state
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // meta flags to control UI
  const [hasDraft, setHasDraft] = useState<boolean>(false);
  const [hasPublished, setHasPublished] = useState<boolean>(false);
  const titleLocked = !!id && hasPublished;

  // errors
  const [titleErr, setTitleErr] = useState<string | null>(null);
  const [predErr, setPredErr] = useState<string | null>(null);

  // dependency loop dialog state
  const [cycleOpen, setCycleOpen] = useState(false);
  const [cycleTitles, setCycleTitles] = useState<string[] | null>(null);
  const [cycleIds, setCycleIds] = useState<string[] | null>(null);

  // dependency graph state
  const [graph, setGraph] = useState<{ nodes: any[]; edges: any[] } | null>(
    null
  );

  // Image cropper state
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null); // data URL or existing URL preview
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [showCropper, setShowCropper] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null); // store area from onCropComplete
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal state for “View” (layers / annexures)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMD, setModalMD] = useState(""); // markdown content from Firestore

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert("File size exceeds 2 MB limit");
      return;
    }

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setImageSrc(reader.result as string);
      setShowCropper(true);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setCroppedAreaPixels(null);
    });
    reader.readAsDataURL(file);
  };

  // Capture the area; crop happens on "Done"
  const onCropComplete = useCallback((_: any, area: any) => {
    setCroppedAreaPixels(area);
  }, []);

  const handleCropSave = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    const img = await getCroppedImg(imageSrc, croppedAreaPixels);
    setCroppedImage(img); // data URL
    setShowCropper(false);
  }, [imageSrc, croppedAreaPixels]);

  const disabledAll = loading || saving;

  const mustChoosePredecessor = templateOptions.length > 0; 
  const predecessorChosen =
    hasPredecessors === "no"
      ? true
      : predecessorIds.some((p) => p && p.trim().length > 0);

const canSubmit =
  titleErr === null &&
  price !== "" &&
  Number(price) >= 0 &&
  Number(price) <= 999 &&
  structure.trim().length > 0 &&
  openingInitialValue.trim().length > 0 &&
  closingInitialValue.trim().length > 0 &&
  (!mustChoosePredecessor || predecessorChosen);


  // -----------------------
  // load lists + templates + dependency graph
  // -----------------------
  useEffect(() => {
    let graphData;
    (async () => {
      try {
        // 1) published dependency graph
        try {
          const functions = getFunctions();
          const fnGraph = httpsCallable(functions, "templateManager");
          const graphRes: any = await fnGraph({action: "dependencyGraph"});
          graphData = graphRes?.data ?? { nodes: [], edges: [] };
          setGraph(graphData);
        } catch (err) {
          console.warn("Could not fetch published dependency graph:", err);
          setGraph({ nodes: [], edges: [] });
        }

        // 2) Load layers, templates, annexures
        const layersQ = query(
          collection(db, "layers", "meta", "data"),
          orderBy("layerNoKey"),
          limit(200)
        );
        const tplsQ = query(
          collection(db, "templates", "meta", "data"),
          orderBy("titleKey"),
          limit(200)
        );
        const annexQ = query(
          collection(db, "annexures", "meta", "data"),
          orderBy("number"),
          limit(300)
        );

        const [lSnap, tSnap, aSnap] = await Promise.all([
          getDocs(layersQ),
          getDocs(tplsQ),
          getDocs(annexQ),
        ]);

        // This array will contain ONLY { id, title }
        const publishedList: { id: string; title: string }[] = [];

        await Promise.all(
          tSnap.docs.map(async (docSnap) => {
            const templateId = docSnap.id;

            // Check if published version exists
            const publishedRef = doc(
              db,
              "templates",
              "meta",
              "data",
              templateId,
              "versions",
              "published"
            );

            const publishedSnap = await getDoc(publishedRef);

            if (publishedSnap.exists()) {
              const data = docSnap.data() as any;
              publishedList.push({
                id: templateId,
                title: data.title ?? "(untitled)",
              });
            }
          })
        );

        setLayers(
          lSnap.docs.map((d) => {
            const x = d.data() as any;
            return {
              id: d.id,
              layerNo: x.layerNo ?? "",
              title: x.title ?? "",
              short: x.short ?? "",
            };
          })
        );

        setTemplateOptions(
          publishedList
        );

        setAnnexures(
          aSnap.docs.map((d) => {
            const x = d.data() as any;
            return {
              id: d.id,
              number: x.number ?? "",
              title: x.title ?? "",
              requiresConsent: !!x.consent,
            };
          })
        );

        // 3) Defaults for CREATE (no id)
        if (!id) {
          // if any published exists in graph, turn predecessors on by default
          const anyPublishedExists = (graphData?.nodes?.length ?? 0) > 0;
          console.log("any published", anyPublishedExists)
          setHasPredecessors(anyPublishedExists ? "yes" : "no");
          setPredecessorIds([""]);
        }

        // 4) If editing, load this template (meta + version)
        if (id) {
          const metaRef = doc(db, "templates", "meta", "data", id);
          const metaSnap = await getDoc(metaRef);

          if (metaSnap.exists()) {
            const m: any = metaSnap.data();
            const _hasDraft = !!m.hasDraft;
            const _hasPublished = !!m.hasPublished;
            setHasDraft(_hasDraft);
            setHasPublished(_hasPublished);

            // prefer draft content if present, else published
            const versionPath = _hasDraft ? "draft" : "published";
            const verRef = doc(
              db,
              "templates",
              "meta",
              "data",
              id,
              "versions",
              versionPath
            );
            const verSnap = await getDoc(verRef);

            const data =
              verSnap.exists()
                ? verSnap.data()
                : (
                    await getDoc(
                      doc(
                        db,
                        "templates",
                        "meta",
                        "data",
                        id,
                        "versions",
                        _hasDraft ? "published" : "draft"
                      )
                    )
                  ).data() ?? {};
            console.log("data", data)
            setTitle((data?.title as string) ?? (m.title ?? ""));
            setShortDesc((data?.shortDesc as string) ?? "");
            setPrice(
              data?.price === 0 || typeof data?.price === "number"
                ? String(data.price)
                : ""
            );
            setStructure(
              typeof data?.structure === "string"
                ? data?.structure
                : JSON.stringify(data?.structure ?? "", null, 2)
            );

            const layerIds: string[] = Array.isArray(data?.layerIds) ? data.layerIds : [];
            setSelectedLayerIds(new Set(layerIds));

            // NEW: annexureIds from version
            const aIds: string[] = Array.isArray((data as any)?.annexureIds)
              ? (data as any).annexureIds
              : [];
            setSelectedAnnexureIds(new Set(aIds));

            // predecessors
            const preds: string[] = Array.isArray(data?.predecessorIds) ? data.predecessorIds : [];
            setHasPredecessors(preds.length ? "yes" : "no");
            setPredecessorIds(preds.length ? preds : [""]);

            // flags + sections
            if (typeof data?.isRelationShipReport === "boolean") {
              setIsRelationShipReport(data.isRelationShipReport ? "yes" : "no");
            }
            if (typeof data?.openingText === "string") {
              setOpeningInitialValue(data.openingText);
            }
            if (typeof data?.closingText === "string") {
              setClosingInitialValue(data.closingText);
            }

            // image preview
            if (typeof (data as any)?.cardBgUrl === "string" && (data as any).cardBgUrl.length > 0) {
              setCroppedImage((data as any).cardBgUrl);
            }
          } else {
            const anyPublishedExists = (graphData?.nodes?.length ?? 0) > 0;
            setHasPredecessors(anyPublishedExists ? "yes" : "no");
            setPredecessorIds([""]);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // fetch published dependency graph on-demand (reusable)
  const fetchGraph = async () => {
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "templateGetDependencyGraph");
      const res: any = await fn();
      const graphData = res?.data ?? { nodes: [], edges: [] };
      setGraph(graphData);
      return graphData;
    } catch (err) {
      console.error("Failed to fetch dependency graph:", err);
      return { nodes: [], edges: [] };
    }
  };

  const ensureGraph = async () => {
    if (graph && Array.isArray(graph.nodes)) return graph;
    try {
      const g = await fetchGraph();
      return g;
    } catch {
      return { nodes: [], edges: [] };
    }
  };

  const openCycleDialog = async (idsArg?: string[] | null, titlesArg?: string[] | null) => {
    const g = await ensureGraph();
    if (idsArg && idsArg.length > 0) {
      setCycleIds(idsArg);
      if (titlesArg && titlesArg.length > 0) setCycleTitles(titlesArg);
      else {
        const mapped = idsArg.map((id) => {
          const node = (g?.nodes ?? []).find((n: any) => n.id === id);
          return node?.title ?? id;
        });
        setCycleTitles(mapped);
      }
    } else {
      const nodes = (g?.nodes ?? []);
      if (nodes.length === 0) {
        setCycleIds([]);
        setCycleTitles([]);
      } else {
        const ids = nodes.map((n: any) => n.id);
        const titles = nodes.map((n: any) => n.title ?? n.id);
        setCycleIds(ids);
        setCycleTitles(titles);
      }
    }
    setCycleOpen(true);
  };

  // rows for layers table (unchanged UI)
  const rows = useMemo(
    () =>
      layers.map((l) => ({
        id: l.id,
        code: l.layerNo,
        name: l.title,
        short: l.short ?? "",
      })),
    [layers]
  );

  const noLayers = !loading && rows.length === 0;

  const toggleLayer = (lid: string) =>
    setSelectedLayerIds((prev) => {
      const next = new Set(prev);
      next.has(lid) ? next.delete(lid) : next.add(lid);
      return next;
    });

  // NEW: toggle for annexures
  const toggleAnnexure = (aid: string) =>
    setSelectedAnnexureIds((prev) => {
      const next = new Set(prev);
      next.has(aid) ? next.delete(aid) : next.add(aid);
      return next;
    });

  const setPredAt = (i: number, v: string) =>
    setPredecessorIds((arr) => {
      const copy = [...arr];
      copy[i] = v;
      return copy;
    });

  const addPred = () => setPredecessorIds((a) => [...a, ""]);
  const removePred = (i: number) => setPredecessorIds((a) => a.filter((_, idx) => idx !== i));

  // “View” handlers (load markdown then show modal)
  const viewLayer = async (layerId: string) => {
    try {
      const ref = doc(db, "layers", "meta", "data", layerId);
      const snap = await getDoc(ref);
      const d: any = snap.data() || {};
      const md: string =
        d.instructions ||
        d.body ||
        d.long ||
        d.short ||
        "No instructions found.";
      setModalTitle(`${d.layerNo ?? ""} ${d.title ?? "Layer"}`);
      setModalMD(md);
      setModalOpen(true);
    } catch {
      setModalTitle("Layer");
      setModalMD("No instructions found.");
      setModalOpen(true);
    }
  };

  const viewAnnexure = async (annexId: string) => {
    try {
      const ref = doc(db, "annexures", "meta", "data", annexId);
      const snap = await getDoc(ref);
      const d: any = snap.data() || {};
      const md: string = d.instructions || d.body || "No instructions found.";
      setModalTitle(`${d.number ?? ""} ${d.title ?? "Annexure"}`);
      setModalMD(md);
      setModalOpen(true);
    } catch {
      setModalTitle("Annexure");
      setModalMD("No instructions found.");
      setModalOpen(true);
    }
  };

  // 🔧 upsert now calls unified handler; includes annexureIds
  const upsert = async (extra?: any): Promise<string | null> => {
   
    setTitleErr(null);
    setPredErr(null);
    setCycleTitles(null);
    setCycleIds(null);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, "templateManager");
      const payload = {
        action: "upsertDraft",
        id: id ?? undefined,
        content: {
          title: title.trim(),
          titleKey: normKey(title),
          shortDesc: shortDesc.trim(),
          price: Number(price), // can be 0
          isRelationShipReport: isRelationShipReport === "yes",
          layerIds: Array.from(selectedLayerIds),
          annexureIds: Array.from(selectedAnnexureIds), // <— NEW
          predecessorIds:
            hasPredecessors === "yes"
              ? Array.from(new Set(predecessorIds.filter(Boolean)))
              : [],
          structure: (() => {
            try {
              return JSON.parse(structure);
            } catch {
              return structure.trim();
            }
          })(),
          openingText: (() => {
            try {
              return JSON.parse(openingInitialValue);
            } catch {
              return openingInitialValue.trim();
            }
          })(),
          closingText: (() => {
            try {
              return JSON.parse(closingInitialValue);
            } catch {
              return closingInitialValue.trim();
            }
          })(),
          ...(extra?.cardBgUrl ? { cardBgUrl: extra.cardBgUrl } : {}),
        },
      };
      if (payload.content.cardBgUrl === null) {
        delete payload.content.cardBgUrl;
      }
      const res: any = await fn(payload);
      return res?.data?.id ?? id ?? null;
    } catch (err: any) {
      const code = err?.code || "";
      const details = err?.details || {};
      const msg = (err?.message as string) ?? "";

      if (code === "already-exists" || msg.includes("DUP_TITLE")) {
        setTitleErr("This report title already exists.");
      } else if (
        code === "failed-precondition" &&
        (msg.includes("TITLE_LOCKED") || msg.includes("TITLE_LOCKED_AFTER_PUBLISH"))
      ) {
        setTitleErr("Title is locked after first publish.");
      } else if (
        (code === "failed-precondition" || code === "functions/failed-precondition") &&
        (msg.includes("CYCLE_DETECTED") || details?.cycleTitles || details?.cyclePath)
      ) {
        const titles = (details.cycleTitles as string[] | undefined) ?? null;
        const ids = (details.cyclePath as string[] | undefined) ?? null;
        setCycleTitles(titles);
        setCycleIds(ids);
        setPredErr("Dependency loop detected.");
      } else {
        setPredErr("Failed to save draft.");
      }
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handlePriceChange = (value: string) => {
    // Empty allowed
    if (value === "") {
      setPrice("");
      setPriceError("");
      return;
    }
  
    const num = Number(value);
  
    // Invalid number or negative
    if (isNaN(num) || num < 0) {
      setPrice(value);
      setPriceError("Price cannot be negative.");
      return;
    }
  
    // Too high
    if (num > 999) {
      setPrice(value);
      setPriceError("Maximum allowed price is 999.");
      return;
    }
  
    // valid
    setPrice(value);
    setPriceError("");
  };

  // ✅ Save Draft: if croppedImage exists, upload to Storage and include cardBgUrl
  const onSaveDraft = async () => {
    if (!canSubmit || disabledAll) return;

    let extra: any = undefined;
    setSaving(true);
    if (croppedImage) {
      try {
        const storage = getStorage();
        const fileId = id ?? String(Date.now());
        const path = `template-cards/${fileId}.jpg`;
        const imgRef = storageRef(storage, path);
        if (croppedImage.startsWith("data:")) {
          console.log("here0")
          await uploadString(imgRef, croppedImage, "data_url");
          console.log("here1")
          const url = await getDownloadURL(imgRef);
          extra = { cardBgUrl: url };
        } else {
          extra = { cardBgUrl: croppedImage };
        }
      } catch (e) {
        console.log("here3")
        console.warn("Image upload failed; continuing without cardBgUrl", e);
      }
      console.log("here4")
    }

    const newId = await upsert(extra);
    setSaving(false);
    if (newId) navigate(`/admin/templates/${newId}/?ver=draft`);
  };

  return (
    <div className="mx-auto max-w-6xl px-6 pt-6 pb-10">
      <fieldset disabled={disabledAll} aria-busy={disabledAll} className={disabledAll ? "opacity-60 select-none" : ""}>
        {/* Header */}
        <button
          onClick={() => navigate("/admin/templates")}
          className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Templates
        </button>

        <div className="mb-4 mt-4 flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-zinc-800">{id ? "Edit template" : "Create template"}</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/templates")}
              className="rounded-lg border border-zinc-300 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              onClick={onSaveDraft}
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-600/90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
            >
              Save draft
            </button>
          </div>
        </div>

        {/* BASICS */}
        <div className="mb-2 grid grid-cols-1 gap-6 border-y border-gray-200 divide-y divide-gray-200">
          {/* Title */}
          <div className="flex items-center justify-between py-8">
            <div className="basis-[45%]">
              <label className="block text-sm font-medium text-zinc-700">
                Report title <span className="text-rose-600">*</span>
              </label>
              <label className="text-xs text-zinc-500">
                Displayed to users. Must be unique (pre-publish).{" "}
                {titleLocked && (
                  <span className="text-amber-600">Title is locked because a published version exists.</span>
                )}
              </label>
            </div>
            <div className="basis-[50%]">
              <input
                value={title}
                maxLength={100}
                onChange={(e) => {
                  console.log(/.*\S+.*/.test(e.target.value))
                  if(!/^(?:.*\S){3,}.*$/.test(e.target.value) || e.target.value.length < 3){
                    setTitleErr("Enter a valid title")
                  }else{
                    setTitleErr(null)
                  }
                  setTitle(e.target.value)
                }}
                placeholder="Up to 100 characters"
                className={`field-grey ${titleLocked ? "opacity-70" : ""}`}
                disabled={titleLocked}
                title={titleLocked ? "Title can't be changed after first publish" : undefined}
              />
              {titleErr ? (
                <p className="mt-1 ml-2 text-xs text-rose-600">{titleErr}</p>
              ) : (
                <p className="mt-1 ml-2 text-xs text-zinc-400">Up to 100 characters *</p>
              )}
            </div>
          </div>

          {/* Short description */}
          <div className="flex items-center justify-between py-8">
            <div className="basis-[45%]">
              <label className="block text-sm font-medium text-zinc-700">
                Short description <span className="text-rose-600">*</span>
              </label>
              <label className="text-xs text-zinc-500">Displayed to users.</label>
            </div>
            <div className="basis-[50%]">
              <textarea
                rows={4}
                maxLength={500}
                value={shortDesc}
                onChange={(e) => setShortDesc(e.target.value)}
                placeholder="Up to 500 characters"
                className="field-grey"
              />
              <p className="mt-1 ml-2 text-xs text-zinc-400">Up to 500 characters *</p>
            </div>
          </div>

          {/* Card image */}
          <div className="flex items-center justify-between py-8">
            <div className="basis-[45%]">
              <label className="block text-sm font-medium text-zinc-700">
                Card background image (optional)
              </label>
              <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                Ideally 3:2 aspect. Max 2 MB. You’ll crop before saving.
              </p>
            </div>

            <div className="basis-[45%] flex justify-center">
              <div
                className="relative w-48 h-72 rounded-xl overflow-hidden shadow-md flex items-center justify-center cursor-pointer bg-gradient-to-b from-white to-purple-600"
                onClick={() => fileInputRef.current?.click()}
              >
                {croppedImage ? (
                  <img
                    src={croppedImage}
                    alt="Preview"
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <button
                    type="button"
                    className="bg-white text-purple-600 font-medium px-4 py-2 rounded-lg shadow-sm hover:bg-purple-50 transition"
                  >
                    Upload
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onFileChange}
                />
              </div>
            </div>

            {showCropper && (
              <div className="fixed inset-0 bg-black/60 flex flex-col items-center justify-center z-50">
                <div className="relative w-[90%] h-[70vh] bg-white rounded-lg overflow-hidden">
                  <div className="relative w-full h-full">
                    <Cropper
                      image={imageSrc!}
                      crop={crop}
                      zoom={zoom}
                      aspect={2 / 3}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                </div>

                <div className="flex gap-4 mt-4">
                  <button
                    onClick={() => setShowCropper(false)}
                    className="px-4 py-2 bg-gray-300 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropSave}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Price */}
          <div className="flex items-center justify-between py-8">
  <div className="basis-[45%]">
    <label className="block text-sm font-medium text-zinc-700">
      Price (USD) <span className="text-rose-600">*</span>
    </label>
    <label className="text-xs text-zinc-500">
      Templates can be free—enter 0 for free.
    </label>
  </div>

  <div className="basis-[50%]">
    <input
      type="number"
      min="0"
      max="999"
      value={price}
      onChange={(e) => handlePriceChange(e.target.value)}
      className={`field-grey w-full ${priceError ? "border-red-500" : ""}`}
    />

    {priceError && (
      <p className="text-red-600 text-xs mt-1">{priceError}</p>
    )}
  </div>
</div>


          {/* Relationship flag */}
          <div className="flex items-center justify-between py-8">
            <div className="basis-[45%]">
              <label className="block text-sm font-medium text-zinc-700">
                Is this a relationship  report <span className="text-rose-600">*</span>
              </label>
            </div>
            <div  className=" basis-[50%] mb-4 flex items-center gap-6 text-sm text-zinc-700">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="pred"
                    value="yes"
                    checked={isRelationShipReport === "yes"}
                    onChange={() => setIsRelationShipReport("yes")}
                    className="accent-brand-600"
                  />
                  Yes
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="pred"
                    value="no"
                    checked={isRelationShipReport === "no"}
                    onChange={() => setIsRelationShipReport("no")}
                    className="accent-brand-600"
                  />
                  No
                </label>
              </div>
          </div>

          {/* PREDECESSORS */}
          { hasPredecessors === "yes" && (<div className="flex items-center justify-between py-8">
            <div className="basis-[45%]">
              <h3 className="mb-2 text-sm font-semibold text-zinc-800">Predecessors <span className="text-rose-600">*</span></h3>
              <p className="mb-3 text-xs text-zinc-500">Does this report need other report(s) first?</p>

              {/* <Button onClick={async () => await openCycleDialog()}>View Dependency Tree</Button> */}
            </div>

            <div className="basis-[50%]">
                <div className="space-y-3">
                  {predecessorIds.map((v, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Select
                        value={v}
                        onChange={(e) => setPredAt(i, e.target.value)}
                        className=" max-w-sm select-padding"
                      >
                        <option value="">Select a template…</option>
                        {templateOptions
                          .filter((opt) => {
                            // allow showing current selected value in its own dropdown
                            return !predecessorIds.includes(opt.id) || opt.id === v;
                          })
                          .map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.title}
                            </option>
                          ))}
                      </Select>
                      <button
                        onClick={() => removePred(i)}
                        className="rounded-md border border-zinc-300 px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  {predErr && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-rose-600">
                      <span>{predErr}</span>
                      {(cycleTitles?.length ?? 0) > 0 && (
                        <button
                          type="button"
                          onClick={async () =>
                            await openCycleDialog(cycleIds ?? [], cycleTitles ?? [])
                          }
                          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-rose-700 hover:bg-rose-50"
                          title="View dependency loop"
                        >
                          <InformationCircleIcon className="h-4 w-4" />
                          View loop
                        </button>
                      )}
                    </div>
                  )}

                  <button
                    onClick={addPred}
                    className="rounded-lg bg-brand-50 px-3 py-1.5 text-sm text-brand-700 hover:bg-brand-100"
                  >
                    Add another
                  </button>
                </div>
            </div>
          </div>)}
        </div>

        <div className="divide-y divide-gray-200">

          {/* MAP TO LAYERS */}
          <div className="py-8">
            <div className="mb-3 ml-1">
              <h3 className="text-sm font-semibold text-zinc-800">Map report to layers</h3>
              <p className="text-xs text-zinc-500 mt-2">Link one or more layers to the report.</p>
            </div>

            {noLayers ? (
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-700 mt-4">
                No layers found. You need at least one layer to map a template.
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-zinc-200 mt-4">
                <table className="min-w-full text-sm">
                  <thead className="bg-zinc-50 text-zinc-500">
                    <tr>
                      <th className="w-10 px-3 py-2 text-left"></th>
                      <th className="px-4 py-2 text-left font-medium">Layer</th>
                      <th className="px-4 py-2 text-left font-medium">Name</th>
                      <th className="px-4 py-2 text-left font-medium">Short description</th>
                      <th className="px-4 py-2 text-left font-medium">Action</th> {/* NEW */}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {rows.map((r) => {
                      const checked = selectedLayerIds.has(r.id);
                      return (
                        <tr key={r.id} className="hover:bg-zinc-50">
                          <td className="px-3">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleLayer(r.id)}
                              className="h-4 w-4 accent-brand-600"
                            />
                          </td>
                          <td className="px-4 py-2 text-zinc-700">{r.code}</td>
                          <td className="px-4 py-2 text-zinc-700">{r.name}</td>
                          <td className="px-4 py-2 text-zinc-500">{r.short || "—"}</td>
                          <td className="px-4 py-2">
                            <button
                              type="button"
                              onClick={() => viewLayer(r.id)}
                              className="rounded-lg border border-zinc-300 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* MAP TO ANNEXURES (NEW) */}
          <div className="py-8">
            <div className="mb-3 ml-1">
              <h3 className="text-sm font-semibold text-zinc-800">Map report to annexures</h3>
              <p className="text-xs text-zinc-500 mt-2">
                Annexures, or conditional layers, are executed after the baseline report. You have the option to include or exclude annexures from the final report.
              </p>
            </div>

            <div className="overflow-hidden rounded-xl border border-zinc-200 mt-4">
              <table className="min-w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-500">
                  <tr>
                    <th className="w-10 px-3 py-2 text-left"></th>
                    <th className="px-4 py-2 text-left font-medium">Layer</th>
                    <th className="px-4 py-2 text-left font-medium">Name</th>
                    <th className="px-4 py-2 text-left font-medium">Requires member&apos;s consent?</th>
                    <th className="px-4 py-2 text-left font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {annexures.map((a) => {
                    const checked = selectedAnnexureIds.has(a.id);
                    return (
                      <tr key={a.id} className="hover:bg-zinc-50">
                        <td className="px-3">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAnnexure(a.id)}
                            className="h-4 w-4 accent-brand-600"
                          />
                        </td>
                        <td className="px-4 py-2 text-zinc-700">{a.number}</td>
                        <td className="px-4 py-2 text-zinc-700">{a.title}</td>
                        <td className="px-4 py-2 text-zinc-700">{a.requiresConsent ? "Yes" : "No"}</td>
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            onClick={() => viewAnnexure(a.id)}
                            className="rounded-lg border border-zinc-300 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* STRUCTURE */}
          <div className="py-8">
            <h3 className="mb-2 text-sm font-semibold text-zinc-800">
              Template structure <span className="text-rose-600">*</span>
            </h3>
            <textarea
              rows={14}
              value={structure}
              onChange={(e) => setStructure(e.target.value)}
              className="field-grey"
            />
          </div>

          {/* Opening Section */}
          <div className="py-8">
            <MarkdownSectionEditor
              initialMarkdown={openingInitialValue}
              onSave={(v: any) => setOpeningInitialValue(v)}
              title="Opening section"
              readOnly={false}
            />
          </div>

          {/* Closing Section */}
          <div className="py-8">
            <MarkdownSectionEditor
              initialMarkdown={closingInitialValue}
              onSave={(v: any) => setClosingInitialValue(v)}
              title="Closing section"
              readOnly={false}
            />
          </div>
        </div>
      </fieldset>

      {/* Dependency Loop Dialog */}
      <CycleDialog
        open={cycleOpen}
        onClose={() => setCycleOpen(false)}
        titles={cycleTitles ?? []}
        ids={cycleIds ?? []}
        currentTitle={title}
        graph={graph}
      />

      {/* View Modal */}
      <SimpleModal open={modalOpen} title={modalTitle} onClose={() => setModalOpen(false)}>
        <div className="prose prose-zinc max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {modalMD.replace(/\\\\n/g, "\n").replace(/\\n/g, "\n")}
          </ReactMarkdown>
        </div>
      </SimpleModal>
    </div>
  );
}
